import { useEffect, useState } from 'react';
import { Loader2, Copy, Download, ShieldCheck, AlertTriangle } from 'lucide-react';
import {
  mfaEnrollStart, mfaEnrollVerify, getDisplayErrorMessage, isRateLimited,
} from '@/lib/api';
import { getMfaFlow, clearMfaFlow, finishedSignInPath } from '@/lib/mfaFlow';
import { takeReturnPath } from '@/lib/returnPath';
import { secretFromOtpAuthUri, formatSecretForDisplay } from '@/lib/otpauth';
import { generateQrSvg } from '@/lib/qr-generator';
import { useAuthStore } from '@/store/auth';
import { AuthLayout } from '@/components/AuthLayout';
import formStyles from '@/components/AuthForm.module.css';
import styles from './MfaEnrollPage.module.css';
import type { LoginResponse } from '@evi/shared';

interface Props {
  onNavigate: (path: string) => void;
}

/**
 * MFA enrollment screen (commit 4 of the admin-MFA arc). Reached
 * when login or magic-verify returns the MFA-pending shape with
 * variant 'enroll' — dormant until commit 5 arms the server.
 *
 * Two phases:
 *   setup — fetch the otpauth URI via enroll/start, render the QR
 *           (generateQrSvg, the QrModal pattern) plus the parsed
 *           base32 secret for manual entry, take the first 6-digit
 *           code, exchange via enroll/verify.
 *   codes — the ten one-time backup codes from the verify response,
 *           shown ONCE. Copy + download + an explicit acknowledgment
 *           gate: the session from the response is NOT stored until
 *           the admin confirms they saved the codes, so the recovery
 *           path can't be skipped past by accident.
 *
 * The pending token lives in the in-memory flow state only — a
 * refresh loses it and this page bounces to /login (re-entering
 * primary auth is the right recovery for a 5-minute token).
 */
export function MfaEnrollPage({ onNavigate }: Props) {
  const setAuth = useAuthStore((s) => s.setAuth);
  const flow = getMfaFlow();

  const [qrSvg, setQrSvg] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);
  // Set on verify success; holds the session + codes through the
  // acknowledgment gate. setAuth fires only after the admin
  // confirms the codes are saved.
  const [enrolled, setEnrolled] = useState<(LoginResponse & { backup_codes: string[] }) | null>(null);
  const [acked, setAcked] = useState(false);
  const [copied, setCopied] = useState(false);

  // No flow state (refresh, deep link, wrong variant) → restart at
  // login. Effect-wrapped so render stays pure.
  useEffect(() => {
    if (!flow || flow.variant !== 'enroll') onNavigate('/login');
  }, [flow, onNavigate]);

  useEffect(() => {
    if (!flow || flow.variant !== 'enroll') return;
    mfaEnrollStart(flow.pendingToken)
      .then(async ({ otpauth_uri }) => {
        setSecret(secretFromOtpAuthUri(otpauth_uri));
        setQrSvg(await generateQrSvg(otpauth_uri));
      })
      .catch(async (err: unknown) => {
        setStartError(await getDisplayErrorMessage(err, 'Could not start enrollment. Sign in again.'));
      });
    // flow is module-state read once per mount; the bounce effect
    // above handles its absence.
  }, []);

  if (!flow || flow.variant !== 'enroll') return null;

  const onSubmitCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting || code.trim().length < 6) return;
    setSubmitting(true);
    setCodeError(null);
    try {
      setEnrolled(await mfaEnrollVerify(flow.pendingToken, code.trim()));
    } catch (err) {
      if (isRateLimited(err)) {
        setCodeError('Too many attempts. Wait a moment, then try again.');
      } else {
        setCodeError(await getDisplayErrorMessage(err, 'Invalid verification code'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const copyCodes = async () => {
    if (!enrolled) return;
    try {
      await navigator.clipboard.writeText(enrolled.backup_codes.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard can be unavailable (permissions); download remains.
    }
  };

  const downloadCodes = () => {
    if (!enrolled) return;
    const blob = new Blob(
      [`EVI admin backup codes for ${enrolled.user.email}\nEach code works once.\n\n${enrolled.backup_codes.join('\n')}\n`],
      { type: 'text/plain' },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'evi-admin-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  const finish = () => {
    if (!enrolled || !acked) return;
    // The invite's next page (a new partner lands on New job), else the role's landing page.
    const next = flow?.next;
    setAuth(enrolled.user, enrolled.access_token);
    clearMfaFlow();
    // The invite's next page first (it is why this person is signing in), then a link they opened signed out
    // (lib/returnPath.ts), then the role's landing page.
    onNavigate(finishedSignInPath(enrolled.user, next ?? takeReturnPath() ?? undefined));
  };

  // ── Phase 2: backup codes + acknowledgment gate ────────────────────
  if (enrolled) {
    return (
      <AuthLayout
        title="Save your backup codes"
        subtitle="Each code signs you in once if you lose your authenticator"
      >
        <div className={styles.stack}>
          <div className={styles.warnBanner} role="alert">
            <AlertTriangle size={16} aria-hidden />
            These codes are shown only once. Store them somewhere safe before continuing.
          </div>

          <div className={styles.codesGrid} aria-label="Backup codes">
            {enrolled.backup_codes.map((c) => (
              <code key={c} className={styles.codeChip}>{c}</code>
            ))}
          </div>

          <div className={styles.codeActions}>
            <button type="button" className={formStyles.btnSecondary} onClick={() => { void copyCodes(); }}>
              <Copy size={14} aria-hidden /> {copied ? 'Copied' : 'Copy all'}
            </button>
            <button type="button" className={formStyles.btnSecondary} onClick={downloadCodes}>
              <Download size={14} aria-hidden /> Download .txt
            </button>
          </div>

          <label className={styles.ackRow}>
            <input
              type="checkbox"
              checked={acked}
              onChange={(e) => setAcked(e.target.checked)}
            />
            <span>I&apos;ve saved these codes somewhere safe</span>
          </label>

          <button
            type="button"
            className={formStyles.submitBtn}
            disabled={!acked}
            onClick={finish}
          >
            <ShieldCheck size={16} aria-hidden /> Continue
          </button>
        </div>
      </AuthLayout>
    );
  }

  // ── Phase 1: QR + first code ───────────────────────────────────────
  return (
    <AuthLayout
      title="Set up two-factor authentication"
      subtitle="Scan the code with your authenticator app, then enter the 6-digit code it shows"
    >
      <div className={styles.stack}>
        {startError ? (
          <div className={styles.stack}>
            <div className={formStyles.serverError} role="alert">{startError}</div>
            <button
              type="button"
              className={formStyles.btnSecondary}
              onClick={() => onNavigate('/login')}
            >
              Back to sign-in
            </button>
          </div>
        ) : (
          <>
            <div className={styles.qrBox}>
              {qrSvg
                // Same inline-SVG injection QrModal uses: the string
                // comes from the local qrcode lib (bit-pixels, not
                // markup), so no XSS surface is introduced.
                ? <div className={styles.qrSvgHost} dangerouslySetInnerHTML={{ __html: qrSvg }} />
                : <Loader2 size={24} className={styles.spin} />}
            </div>

            {secret && (
              <p className={styles.secretLine}>
                Can&apos;t scan? Enter this key manually:{' '}
                <code className={styles.secret}>{formatSecretForDisplay(secret)}</code>
              </p>
            )}

            <form onSubmit={(e) => { void onSubmitCode(e); }} noValidate className={formStyles.form}>
              <div className={formStyles.field}>
                <label htmlFor="mfa-code" className={formStyles.label}>Verification code</label>
                <input
                  id="mfa-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  className={`${formStyles.input} ${styles.codeInput} ${codeError ? formStyles.inputError : ''}`}
                  placeholder="123456"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  autoFocus
                />
                {codeError && <p className={formStyles.error}>{codeError}</p>}
              </div>
              <button
                type="submit"
                className={formStyles.submitBtn}
                disabled={submitting || code.length !== 6}
              >
                {submitting ? 'Verifying…' : 'Verify and enable'}
              </button>
            </form>
          </>
        )}
      </div>
    </AuthLayout>
  );
}
